export type FormatType = "en-EN" | "fr-FR"

/**
 * Classe représentant une horloge locale avec format et fuseau horaire.
 */
export class Clock {
    /** Format d'affichage (locale) utilisé pour toLocaleDateString */
    private readonly format: FormatType
    /** Fuseau horaire utilisé (préfixé avec `Etc/`) */
    private readonly timeZone: string
    /** Chaîne représentant la date/heure actuelle formatée */
    private day: string

    /**
     * Initialise une instance de Clock.
     * @param format - Format de localisation (par défaut `fr-FR`).
     * @param timeZone - Fuseau horaire sous la forme `GMT-1`, `GMT+2`, etc. (par défaut `GMT-1`).
     * Construit `timeZone` en le préfixant par `Etc/` et initialise `day` avec la date/heure formatée.
     */
    constructor (format: FormatType = "fr-FR", timeZone: string = "GMT-1") {
        this.format = format
        this.timeZone = `Etc/${timeZone}`
        this.day = new Date().toLocaleDateString(format, {
            timeZone: this.timeZone,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
        })
    }

    /**
     * Getter qui retourne un objet découpant la chaîne `day` en composants:
     * - hours, minutes, seconds : composantes temporelles (chiffres, 2-digit)
     * - suffix : suffixe AM/PM pour `en-EN` (vide pour `fr-FR`)
     * - day, month, year : composantes de la date selon le format
     *
     * Note: la méthode dépend du format de sortie de `toLocaleDateString` utilisé lors de l'initialisation.
     */
    public get dayFormat() {
        const day = this.day.toString().replaceAll(" ", "/").split("/") // [d m y Time]
        const hour = day[3].split(":")
        const isEn = this.format === "en-EN"
        return {
            hours: hour[0],
            minutes: hour[1],
            seconds: hour[2],
            suffix: isEn ? day[4] : "",
            day: isEn ? day[1] : day[0],
            month: isEn ? day[0] : day[1],
            year: day[2]
        }
    }

    /**
     * Getter qui retourne la date complète en chaîne lisible,
     * incluant le weekday, le mois en toutes lettres, le jour et l'année,
     * formatée selon la locale et le fuseau horaire configurés.
     */
    public get dateString() {
        return new Date().toLocaleDateString(this.format, {
            timeZone: this.timeZone,
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric"
        })
    }

    /**
     * Getter qui indique si c'est le jour (true) ou la nuit (false).
     * - Pour `en-EN` : considère l'heure et le suffixe AM/PM (considère PM à partir d'une certaine heure).
     * - Pour `fr-FR` : considère que c'est le jour si l'heure < 19.
     */
    public get isDay() {
        if (this.format === "en-EN") return !(this.dayFormat.suffix.toUpperCase() === "PM" && Number(this.dayFormat.hours) >= 8)
        return Number(this.dayFormat.hours) < 19;
    }
}